/**
 * <support-hours> — rewrites the server-rendered support hours into the
 * visitor's own timezone, and says whether support is open right now.
 * Used by blocks/support-hours.liquid.
 *
 * The block renders "Weekdays 09:00 – 18:00 KST" in the store's standard
 * timezone (data-timezone, e.g. Asia/Seoul), so the page reads correctly
 * without this script. Here we place the opening and closing time on the
 * relevant day in the standard timezone and read those same instants back in
 * the visitor's timezone with Intl, so daylight saving is right on both sides.
 *
 * Open:   "Online | Weekdays 09:00 – 18:00 GMT+9 (00:00 – 09:00 UTC)"
 * Closed: "Offline (Opens at 09:00 GMT+9)"
 *
 * Note the abbreviation is the VISITOR's, not the store's: someone in Jakarta
 * reads 07:00 – 16:00 GMT+7 for the same shift. The store's own label lives in
 * the `timezone_label` setting and only ever shows when this script cannot run.
 *
 * Intl will not say "KST". For Asia/Seoul it gives "GMT+9" in every locale we
 * ship — only US zones get letter forms like "EDT" — so we print what it gives
 * rather than carrying a hand-written abbreviation table that DST would rot.
 *
 * Any failure (unknown timezone, very old browser) leaves the server text as is.
 */
(function () {
  if (customElements.get('support-hours')) return;

  var WEEKDAY_INDEX = { Sun: 7, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }; // ISO: 1 = Monday … 7 = Sunday

  /** Calendar fields of an instant as seen in a timezone. */
  function partsIn(date, timeZone) {
    var dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      weekday: 'short',
    });
    var out = {};
    dtf.formatToParts(date).forEach(function (part) {
      out[part.type] = part.value;
    });
    return {
      y: +out.year,
      m: +out.month,
      d: +out.day,
      h: +out.hour % 24,
      mi: +out.minute,
      s: +out.second,
      wd: WEEKDAY_INDEX[out.weekday],
    };
  }

  /** Offset of a timezone from UTC, in ms, at a given instant. */
  function offsetAt(date, timeZone) {
    var p = partsIn(date, timeZone);
    var asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s);
    return asUtc - Math.floor(date.getTime() / 1000) * 1000;
  }

  /** The instant at which a wall-clock time occurs in a timezone. */
  function zonedToUtc(y, m, d, h, mi, timeZone) {
    var naive = Date.UTC(y, m - 1, d, h, mi);
    var offset = offsetAt(new Date(naive), timeZone);
    var utc = naive - offset;
    var offsetAgain = offsetAt(new Date(utc), timeZone);
    if (offsetAgain !== offset) utc = naive - offsetAgain;
    return new Date(utc);
  }

  function addDays(y, m, d, n) {
    var t = new Date(Date.UTC(y, m - 1, d + n));
    return [t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()];
  }

  /** ISO weekday (1 = Monday … 7 = Sunday) of a civil date. */
  function isoWeekday(ymd) {
    var g = new Date(Date.UTC(ymd[0], ymd[1] - 1, ymd[2])).getUTCDay();
    return g === 0 ? 7 : g;
  }

  function parseTime(text) {
    var match = /^(\d{1,2}):(\d{2})$/.exec(text || '');
    if (!match) return null;
    return { h: +match[1], mi: +match[2] };
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function safeLocale(code) {
    try {
      new Intl.DateTimeFormat(code);
      return code || undefined;
    } catch (e) {
      return undefined;
    }
  }

  function isTrue(value) {
    return value === 'true';
  }

  class SupportHours extends HTMLElement {
    connectedCallback() {
      try {
        this.render();
      } catch (error) {
        // Leave the server-rendered standard-timezone text in place.
      }
    }

    disconnectedCallback() {
      if (this.timer) clearTimeout(this.timer);
    }

    /**
     * Every shift that could contain or follow "now", as real instants.
     *
     * Built from yesterday to a week out because a shift that closes after
     * midnight (18:00 – 02:00) is still running while the visitor's calendar
     * has already moved on, and because the next opening can be three days
     * away over a weekend. Sorted, so the first match in either loop below is
     * the right one.
     */
    windows(today, days, open, close, zone) {
      var out = [];
      var closesNextDay = close.h * 60 + close.mi <= open.h * 60 + open.mi;
      for (var k = -1; k <= 8; k++) {
        var ymd = addDays(today.y, today.m, today.d, k);
        if (days.indexOf(isoWeekday(ymd)) === -1) continue;
        var end = closesNextDay ? addDays(ymd[0], ymd[1], ymd[2], 1) : ymd;
        out.push({
          start: zonedToUtc(ymd[0], ymd[1], ymd[2], open.h, open.mi, zone),
          end: zonedToUtc(end[0], end[1], end[2], close.h, close.mi, zone),
        });
      }
      return out.sort(function (a, b) {
        return a.start - b.start;
      });
    }

    render() {
      var localEl = this.querySelector('.support-hours__local');
      if (!localEl) return;

      var standardZone = this.dataset.timezone;
      var open = parseTime(this.dataset.open);
      var close = parseTime(this.dataset.close);
      var days = (this.dataset.days || '')
        .split(',')
        .map(function (s) {
          return parseInt(s, 10);
        })
        .filter(function (n) {
          return n >= 1 && n <= 7;
        })
        .sort(function (a, b) {
          return a - b;
        });
      if (!standardZone || !open || !close || !days.length) return;

      var visitorZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!visitorZone) return;
      var locale = safeLocale(this.dataset.locale);
      var use24h = this.dataset.hourFormat !== 'locale';
      var now = new Date();

      var windows = this.windows(partsIn(now, standardZone), days, open, close, standardZone);
      if (!windows.length) return;

      var current = null;
      var next = null;
      for (var i = 0; i < windows.length; i++) {
        if (now >= windows[i].start && now < windows[i].end) {
          current = windows[i];
          break;
        }
      }
      if (!current) {
        for (var j = 0; j < windows.length; j++) {
          if (windows[j].start > now) {
            next = windows[j];
            break;
          }
        }
      }
      if (!current && !next) return;

      var localTime = new Intl.DateTimeFormat(locale, { timeZone: visitorZone, hour: 'numeric', minute: '2-digit' });
      var utcTime = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', hour: 'numeric', minute: '2-digit' });
      function formatIn(date, zone, formatter) {
        if (!use24h) return formatter.format(date);
        var p = partsIn(date, zone);
        return pad(p.h) + ':' + pad(p.mi);
      }
      var formatTime = function (date) {
        return formatIn(date, visitorZone, localTime);
      };
      var formatUtc = function (date) {
        return formatIn(date, 'UTC', utcTime);
      };

      /** What Intl calls the visitor's zone right then — "GMT+9", "EDT". */
      var showZone = isTrue(this.dataset.showZone);
      var zoneAbbr = function (date) {
        if (!showZone) return '';
        try {
          var part = new Intl.DateTimeFormat(locale, { timeZone: visitorZone, timeZoneName: 'short' })
            .formatToParts(date)
            .find(function (p) {
              return p.type === 'timeZoneName';
            });
          return part ? part.value : '';
        } catch (e) {
          return '';
        }
      };

      var text;
      var abbrText = '';
      var utcText = '';
      var noteText = '';

      if (current) {
        // Open: the whole shift on the visitor's clock, then the same two
        // instants in UTC for anyone reading from a third timezone.
        var daysLabel = this.dataset.daysLabel || '';
        var abbr = zoneAbbr(current.start);
        var localRange = formatTime(current.start) + ' – ' + formatTime(current.end);
        var utcRange = formatUtc(current.start) + ' – ' + formatUtc(current.end);
        text = localRange;
        // A NON-breaking space: the abbreviation belongs to the times it
        // qualifies, so a narrow card may move the UTC bracket down a row but
        // must never leave "GMT+7" stranded on its own.
        if (abbr) abbrText = '\u00a0' + abbr;
        if (daysLabel) text = daysLabel + ' ' + text;
        // Only when it adds something. A visitor already on UTC — or on
        // London in winter — was being shown the identical range twice:
        // "00:00 – 09:00 UTC (00:00 – 09:00 UTC)". Compared as rendered text
        // rather than by offset, so it also catches the half-hour zones and
        // whichever clock format is in use.
        if (isTrue(this.dataset.showUtc) && utcRange !== localRange) {
          // No leading space here: it is appended separately below, OUTSIDE
          // this element. Inside, the element's own nowrap would suppress the
          // break, and the bracket could never move to a second row.
          utcText = '(' + utcRange;
          utcText += (this.dataset.utcLabel ? ' ' + this.dataset.utcLabel : '') + ')';
        }
        // Appended after the UTC bracket below, where it has always sat —
        // splitting the abbreviation out of `text` would otherwise have moved
        // the note ahead of it.
        var note = this.dataset.localNote || '';
        if (note) noteText = ' (' + note + ')';
      } else {
        // Closed: only when it opens again. Deliberately no day name — the
        // agreed format is "(Opens at 09:00)". On a Saturday that reads as
        // tomorrow when it means Monday, so `data-opens-at-day` can add the
        // weekday from the theme editor without a code change.
        var opensAt = this.dataset.opensAt || '';
        var when = formatTime(next.start);
        if (isTrue(this.dataset.opensAtDay)) {
          try {
            var weekday = new Intl.DateTimeFormat(locale, { timeZone: visitorZone, weekday: 'short' });
            var dayName = weekday.format(next.start);
            if (dayName !== weekday.format(now)) when = dayName + ' ' + when;
          } catch (e) {
            // Fall back to the bare time.
          }
        }
        var closedAbbr = zoneAbbr(next.start);
        if (closedAbbr) when += ' ' + closedAbbr;
        text = opensAt ? '(' + opensAt + ' ' + when + ')' : '(' + when + ')';
      }

      localEl.textContent = '';
      if (isTrue(this.dataset.showStatus)) {
        var status = document.createElement('span');
        status.className = 'support-hours__status is-' + (current ? 'online' : 'offline');
        status.textContent = (current ? this.dataset.statusOnline : this.dataset.statusOffline) || '';
        if (status.textContent) {
          localEl.appendChild(status);
          // Non-breaking on both sides: the status and the shift it qualifies are
          // one phrase, so a narrow card must not break after "Online |" and drop
          // the hours to a row of their own. That leaves exactly two places this
          // line may break: after the label, and before the UTC bracket.
          localEl.appendChild(document.createTextNode(current ? '\u00a0|\u00a0' : '\u00a0'));
        }
      }
      var hours = document.createElement('span');
      hours.className = 'support-hours__hours';
      hours.textContent = text;
      localEl.appendChild(hours);

      if (abbrText) {
        var zone = document.createElement('span');
        zone.className = 'support-hours__zone';
        zone.textContent = abbrText;
        localEl.appendChild(zone);
      }

      if (utcText) {
        // The one break opportunity on this line, deliberately a bare text
        // node so no element's nowrap can swallow it.
        localEl.appendChild(document.createTextNode(' '));
        var utc = document.createElement('span');
        utc.className = 'support-hours__utc';
        utc.textContent = utcText;
        localEl.appendChild(utc);
      }

      if (noteText) localEl.appendChild(document.createTextNode(noteText));

      // A page left open across 09:00 or 18:00 would keep claiming the old
      // status, so re-render on the boundary itself. Capped at an hour so a
      // clock change or a suspended laptop cannot park the timer for days.
      var boundary = current ? current.end : next.start;
      var delay = Math.min(Math.max(boundary - now + 1000, 1000), 3600000);
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(
        function () {
          try {
            this.render();
          } catch (error) {
            // Keep whatever is on screen.
          }
        }.bind(this),
        delay
      );
    }
  }

  customElements.define('support-hours', SupportHours);
})();
